import * as React from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useApiRequest} from '../../../common/hooks/useApiRequest';
import {useDebouncedValue} from '../../../common/hooks/useDebouncedValue';
import {useDeck} from '../../deck-management/context/DeckContext';
import {WordWithId} from '../../../common/types/words';

export const useNavbarSearchView = () => {

    const {currentDeck} = useDeck();
    const {getRequest} = useApiRequest();

    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedWord, setSelectedWord] = useState<WordWithId | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);

    const debouncedSearchText = useDebouncedValue(searchText, 300);

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    const trimmedSearchText = debouncedSearchText.trim();

    const {data: results = []} = useQuery({
        queryKey: ['word-search', currentDeck?.deckId, trimmedSearchText],
        queryFn: () => getRequest<WordWithId[]>(`decks/${currentDeck?.deckId}/words?search=${encodeURIComponent(trimmedSearchText)}`),
        enabled: isOpen && !!currentDeck && trimmedSearchText.length > 0,
    });

    const lemmaColumnWidth = useMemo(() => {
        const maxLength = results.reduce((max, word) => Math.max(max, word.lemma?.length ?? 0), 0);
        return `${Math.max(maxLength, 1)}ch`;
    }, [results]);

    const posColumnWidth = useMemo(() => {
        const maxLength = results.reduce((max, word) => Math.max(max, word.partOfSpeech.length), 0);
        return `${Math.max(maxLength, 1)}ch`;
    }, [results]);

    const showResults = isOpen && trimmedSearchText.length > 0 && results.length > 0;

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [results]);

    const closeAndReset = () => {
        setIsOpen(false);
        setSearchText('');
        setHighlightedIndex(-1);
    };

    const toggleOpen = () => setIsOpen(prev => !prev);

    const handleResultClick = (word: WordWithId) => {
        setSelectedWord(word);
        closeAndReset();
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            closeAndReset();
            return;
        }

        if (!showResults) {
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev - 1 + results.length) % results.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && highlightedIndex < results.length) {
                handleResultClick(results[highlightedIndex]);
            }
        }
    };

    const closeWordInfoView = () => setSelectedWord(null);

    return {
        isOpen,
        searchText,
        setSearchText,
        selectedWord,
        highlightedIndex,
        setHighlightedIndex,
        inputRef,
        anchorRef,
        results,
        lemmaColumnWidth,
        posColumnWidth,
        showResults,
        toggleOpen,
        closeAndReset,
        handleResultClick,
        handleInputKeyDown,
        closeWordInfoView,
    };
};

export default useNavbarSearchView;
